import { useTranslation } from 'react-i18next';
import { Tag, ValidationMessage } from '@digdir/designsystemet-react';
import { CheckmarkIcon } from '@navikt/aksel-icons';
import type { CustomSurveyQuestion, SubmitCustomSurveyAnswer } from '../../../api/customSurveys';
import { QuestionType } from '../../../api/customSurveyTemplates';
import { ScaleGenerator } from '../../../domain/custom-survey';
import { isAnswered, missingKind } from '../surveyFormView';
import classes from '../CustomSurveyFormSubmission.module.css';

type QuestionFieldProps = {
  question: CustomSurveyQuestion;
  number: number;
  total: number;
  answer?: SubmitCustomSurveyAnswer;
  showError: boolean;
  onChange: (answer: SubmitCustomSurveyAnswer) => void;
};

const TEXT_MAX_LENGTH = { [QuestionType.SHORT_TEXT]: 200, [QuestionType.LONG_TEXT]: 2000 } as const;

const ScaleOptions = ({ question, answer, onChange }: Omit<QuestionFieldProps, 'number' | 'total' | 'showError'>) => {
  const { t } = useTranslation();
  const isNps = question.questionType === QuestionType.NPS_SCALE;
  const scale = question as Extract<CustomSurveyQuestion, { scaleMin: number }>;
  const min = ScaleGenerator.getEffectiveMin(scale.scaleMin, isNps);
  const max = ScaleGenerator.getEffectiveMax(scale.scaleMax, isNps);
  const values = ScaleGenerator.generateRange(min, max);
  const minLabel = scale.scaleMinLabel;
  const maxLabel = scale.scaleMaxLabel;

  return (
    <div className={classes.scale}>
      <div
        className={classes.scaleOptions}
        data-wide={values.length > 6}
        style={values.length > 6 ? undefined : { gridTemplateColumns: `repeat(${values.length}, minmax(0, 1fr))` }}
      >
        {values.map((value) => {
          const extra = value === min && minLabel ? `, ${minLabel}` : value === max && maxLabel ? `, ${maxLabel}` : '';
          return (
            <label key={value} className={classes.scaleOption} data-checked={answer?.numericValue === value}>
              <input
                className="ds-input"
                type="radio"
                name={`question-${question.id}`}
                value={value}
                checked={answer?.numericValue === value}
                onChange={() => onChange({ questionId: question.id, numericValue: value })}
                aria-label={`${t('customSurveys.form.scaleValue', { value, max })}${extra}`}
              />
              <span aria-hidden="true" className={classes.scaleValue}>
                {value}
              </span>
            </label>
          );
        })}
      </div>
      {(minLabel || maxLabel) && (
        <div aria-hidden="true" className={classes.scaleLabels}>
          <span>{minLabel && `${min} = ${minLabel}`}</span>
          <span>{maxLabel && `${max} = ${maxLabel}`}</span>
        </div>
      )}
    </div>
  );
};

const ChoiceOptions = ({ question, answer, onChange }: Omit<QuestionFieldProps, 'number' | 'total' | 'showError'>) => {
  const { t } = useTranslation();
  const isMultiple = question.questionType === QuestionType.MULTIPLE_SELECT;
  const isYesNo = question.questionType === QuestionType.YES_NO;
  const selected = answer?.selectedOptions ?? [];
  const options = isYesNo
    ? [
        { id: 1, label: t('customSurveys.form.yes') },
        { id: 0, label: t('customSurveys.form.no') },
      ]
    : [...question.options].sort((a, b) => a.orderIndex - b.orderIndex).map((option) => ({ id: option.id, label: option.optionText }));

  const isChecked = (id: number) => (isYesNo ? answer?.numericValue === id : selected.includes(id));
  const toggle = (id: number) => {
    if (isYesNo) return onChange({ questionId: question.id, numericValue: id });
    if (!isMultiple) return onChange({ questionId: question.id, selectedOptions: [id] });
    const next = selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id];
    onChange({ questionId: question.id, selectedOptions: next });
  };

  return (
    <div className={isMultiple ? classes.choiceList : classes.choiceGrid} data-yes-no={isYesNo}>
      {options.map((option) => (
        <label key={option.id} className={classes.choice} data-checked={isChecked(option.id)}>
          <input
            className="ds-input"
            type={isMultiple ? 'checkbox' : 'radio'}
            name={`question-${question.id}`}
            value={option.id}
            checked={isChecked(option.id)}
            onChange={() => toggle(option.id)}
            aria-describedby={isMultiple ? `question-${question.id}-hint` : undefined}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
};

const TextInput = ({
  question,
  answer,
  showError,
  onChange,
}: Omit<QuestionFieldProps, 'number' | 'total'>) => {
  const { t } = useTranslation();
  const isLong = question.questionType === QuestionType.LONG_TEXT;
  const value = answer?.textValue ?? '';
  const props = {
    id: `question-${question.id}-input`,
    className: 'ds-input',
    value,
    maxLength: TEXT_MAX_LENGTH[question.questionType as QuestionType.SHORT_TEXT | QuestionType.LONG_TEXT],
    'aria-invalid': showError || undefined,
    'aria-describedby': `question-${question.id}-hint`,
    'aria-label': question.questionText,
    onChange: (event: { target: { value: string } }) =>
      onChange({ questionId: question.id, textValue: event.target.value }),
  };

  return (
    <div className="ds-field">
      {isLong ? (
        <>
          <textarea {...props} rows={5} className={`ds-input ${classes.textarea}`} />
          <p className={classes.charCount} aria-live="polite">
            {t('customSurveys.form.characters', { count: value.length })}
          </p>
        </>
      ) : (
        <input {...props} type="text" autoComplete="off" />
      )}
    </div>
  );
};

/** Ett spørsmål som eget kort, med nummer, hjelpetekst og feilmelding */
export const QuestionField = ({ question, number, total, answer, showError, onChange }: QuestionFieldProps) => {
  const { t } = useTranslation();
  const answered = isAnswered(question, answer);
  const type = question.questionType;
  const isScale = type === QuestionType.LIKERT_SCALE || type === QuestionType.NPS_SCALE;
  const isText = type === QuestionType.SHORT_TEXT || type === QuestionType.LONG_TEXT;
  const hint = type === QuestionType.MULTIPLE_SELECT ? t('customSurveys.form.chooseMany') : isText ? t('customSurveys.form.textHint') : null;

  return (
    <fieldset id={`question-${question.id}`} className={classes.question} data-invalid={showError}>
      <legend className="ds-sr-only">{`${number}. ${question.questionText}`}</legend>
      <div className={classes.questionHead}>
        <div className={classes.questionMeta}>
          <span className={classes.questionNumber}>{t('customSurveys.form.questionOf', { number, total })}</span>
          <span className={classes.questionStatus}>
            {!question.isRequired && (
              <Tag data-size="sm" data-color="neutral">
                {t('customSurveys.form.optional')}
              </Tag>
            )}
            {answered && (
              <span className={classes.answered}>
                <CheckmarkIcon aria-hidden />
                {t('customSurveys.form.answered')}
              </span>
            )}
          </span>
        </div>
        <p aria-hidden="true" className={classes.questionText}>
          {question.questionText}
        </p>
        {hint && (
          <p id={`question-${question.id}-hint`} className={classes.hint}>
            {hint}
          </p>
        )}
      </div>

      {isScale && <ScaleOptions question={question} answer={answer} onChange={onChange} />}
      {isText && <TextInput question={question} answer={answer} showError={showError} onChange={onChange} />}
      {!isScale && !isText && <ChoiceOptions question={question} answer={answer} onChange={onChange} />}

      {showError && (
        <ValidationMessage data-color="danger">{t(`customSurveys.form.errors.${missingKind(question)}`)}</ValidationMessage>
      )}
    </fieldset>
  );
};
